import {
  SUGGESTED_BREEDERS_FOR_APPLICATIONS_LIMIT,
  useCreateApplicationsForSuggestedBreeders,
  useCurrentAccount,
  useFormatBreederSuggestions,
  useGetApplicationForBreederId,
  useGetBreeds,
  useGetBuyerApplications,
  useGetSuggestedBreeders,
} from '@application/api';
import { APPLICATION_STEPS } from '@application/constants';
import {
  formatLanguages,
  scrollToError,
  useModal,
  usePersistHasADog,
  useRouter,
  useSavedApplication,
  useWindowConfirmUnload,
} from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { AccountCreatedFromEnum, DogBreederApplicationOtherAnimal, GenderEnum } from '@gql/graphql';
import { LoadingContainer } from '@presentation/components';
import type { ApplicationPageAuthMethod, ApplicationPageFormValues } from '@presentation/pages';
import { ApplicationPage } from '@presentation/pages';
import { ConfirmationModal } from '@presentation/pages/ApplicationPage/components/ConfirmationModal';
import { InfosModal } from '@presentation/pages/ApplicationPage/components/InfosModal';
import { PrefillModal } from '@presentation/pages/ApplicationPage/components/PrefillModal';
import { useTranslation } from '@presentation/utils';
import { useFormik } from 'formik';
import { omit, uniqBy } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  JoinBuyerFormController,
  LoginFormController,
  SeoController,
  setBreederForOverviewId,
  useBreederOverviewModal,
} from '../..';
import { useCheckoutApplication } from '../../MtApplicationsBuyerPayModalController/api/useCheckoutApplication';
import { useConfirmCreateApplication } from './api/useConfirmCreateApplication';
import { useCreateDraftApplication } from './api/useCreateDraftApplication';
import { useGetBreeder } from './api/useGetBreeder';
import { useGetLatestApplication } from './api/useGetLatestApplication';
import { useValidationSchema } from './useValidationSchema';
import { useRedirect } from './useRedirect';

export const ApplicationPageController = () => {
  const { infoFromCurrentPath, routeLocationDescriptor, language, navigateToRoute } = useRouter();
  const { translate } = useTranslation();
  const {
    modal: confirmationModal,
    show: showConfirmationModal,
    close: closeConfirmationModal,
  } = useModal();

  const { modal: infosModal, show: showInfosModal, close: closeInfosModal } = useModal();
  const { modal: prefillModal, show: showPrefillModal, close: closePrefillModal } = useModal();
  // prefill from a previous application
  const { application: latestApplication, applicationLoading: latestApplicationLoading } =
    useGetLatestApplication();

  const [hasStartedApplication, setHasStartedApplication] = useState(false);
  const [shouldPrefill, setShouldPrefill] = useState(false);

  const breederId = Number(infoFromCurrentPath.params[0]);
  const { applicationValidationSchema } = useValidationSchema();
  const {
    application: applicationForBreederId,
    applicationLoading: applicationForBreederIdLoading,
  } = useGetApplicationForBreederId(breederId);
  const {
    clearOngoingApplication,
    saveApplication,
    application: unfinishedApplication,
    breederId: unfinishedApplicationId,
  } = useSavedApplication();
  const { breederLoading, breeder } = useGetBreeder(breederId);

  const { currentAccount, currentAccountLoading, currentAccountType, currentAccountLoggedIn } =
    useCurrentAccount();

  //TA-515
  const breederUrl = breeder?.translations[0]?.url;
  useRedirect({ breederUrl, currentAccountType });

  const { breedOptions } = useGetBreeds();
  const { confirmCreateApplicationFromForm, confirmCreateApplicationLoading } =
    useConfirmCreateApplication();
  const { createDraftApplicationFromForm, createDraftApplicationLoading } =
    useCreateDraftApplication();
  const { checkoutApplicationAndRedirect, checkoutApplicationLoading } = useCheckoutApplication();

  const [chosenAuth, onChosenAuthChange] = useState<ApplicationPageAuthMethod | undefined>();
  const [currentStep, setCurrentStep] = useState(APPLICATION_STEPS.FORM);
  const [createdApplicationId, setCreatedApplicationId] = useState<number | null>(null);

  const unfinishedApplicationExists = Number(unfinishedApplicationId) === breederId;
  const shouldShowPrefillModal = latestApplication && !unfinishedApplicationExists;
  const shouldShowInfoModal = !shouldShowPrefillModal && !unfinishedApplicationExists;
  const loading = breederLoading || currentAccountLoading || latestApplicationLoading;

  const canSkipPayment =
    !!currentAccount?.accountRemainingDaysForFreeApplications &&
    currentAccount.accountRemainingDaysForFreeApplications > 0;

  const breedOptionsForBreeder = useMemo(() => {
    const breederBreedIds = (breeder?.breederBreeds ?? []).map((breed) => breed.dogBreed.id);
    return breedOptions.filter((breed) => breederBreedIds.includes(breed.value));
  }, [breedOptions, breeder?.breederBreeds]);

  const getInitialValues = useCallback((): ApplicationPageFormValues => {
    if (unfinishedApplicationExists) {
      return unfinishedApplication as ApplicationPageFormValues;
    }

    if (latestApplication && shouldPrefill) {
      const breedPreferenceOption = breedOptionsForBreeder.find(
        (option) => option.value === latestApplication.breedPreference.id,
      );

      return {
        accommodation: latestApplication.accommodation,
        experiences: latestApplication.experiences[0],
        genderPreferences:
          latestApplication.genderPreferences.length === 2
            ? 'none'
            : latestApplication.genderPreferences[0],
        otherAnimals: latestApplication.otherAnimals,
        timeAlone: latestApplication.timeAlone,
        trainers: latestApplication.trainers,
        breedPreferenceId: breedPreferenceOption?.value || undefined!,
        motive: latestApplication.motive || undefined!,
        otherPreferences: latestApplication.otherPreferences || undefined!,
        questions: latestApplication.questions || undefined!,
      };
    }

    return {
      accommodation: undefined!,
      experiences: undefined!,
      genderPreferences: undefined!,
      otherAnimals: undefined!,
      timeAlone: undefined!,
      trainers: undefined!,
      breedPreferenceId:
        breedOptionsForBreeder.length === 1 ? breedOptionsForBreeder[0].value : undefined!,
      motive: undefined!,
      otherPreferences: undefined,
      questions: undefined,
    };
  }, [
    unfinishedApplicationExists,
    latestApplication,
    shouldPrefill,
    breedOptionsForBreeder,
    unfinishedApplication,
  ]);

  const applicationForm = useFormik<ApplicationPageFormValues>({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    onSubmit: () => {
      if (!currentAccount) {
        setCurrentStep(APPLICATION_STEPS.LOGIN);
        Analytics.sendApplyProgressEvent(APPLICATION_STEPS.LOGIN);
      } else {
        setCurrentStep(APPLICATION_STEPS.SUGGESTIONS);
        Analytics.sendApplyProgressEvent(APPLICATION_STEPS.SUGGESTIONS);
      }
    },
    validationSchema: applicationValidationSchema,
  });

  const createDraft = useCallback(async () => {
    const values = {
      ...applicationForm.values,
      genderPreferences:
        applicationForm.values.genderPreferences === 'none'
          ? [GenderEnum.F, GenderEnum.M]
          : applicationForm.values.genderPreferences,
    };
    createDraftApplicationFromForm({
      breederId,
      values,
    });
  }, [applicationForm.values, breederId, createDraftApplicationFromForm]);

  useEffect(() => {
    if (currentStep === APPLICATION_STEPS.SUGGESTIONS) {
      createDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const onNextClickHandler = useCallback(async () => {
    const errors = await applicationForm.validateForm();
    await applicationForm.submitForm();
    scrollToError(errors);
  }, [applicationForm]);

  const { applications, applicationsRefetch } = useGetBuyerApplications(false);
  const { modalHook: breederOverviewModalHook } = useBreederOverviewModal();

  useEffect(() => {
    if (currentAccountLoggedIn) {
      applicationsRefetch();
    }
  }, [applicationsRefetch, currentAccountLoggedIn]);

  // We want to get suggestions based on
  // 1. Current application breeder id and selected breed
  // 2. Previous applications
  // So we get the first then the second and concat them
  const appliedToBreederIds = [...new Set(applications.map((a) => a.dogBreeder.id))];
  const appliedWithBreedIds = [...new Set(applications.map((a) => a.breedPreference.id))];
  const {
    breeders: breedersForCurrentApplication,
    breedersLoading: breedersForCurrentApplicationLoading,
  } = useGetSuggestedBreeders(
    [breederId],
    applicationForm.values.breedPreferenceId ? [applicationForm.values.breedPreferenceId] : [],
  );
  const {
    breeders: breederForPreviousApplications,
    breedersLoading: breedersForPreviousApplicationLoading,
  } = useGetSuggestedBreeders(appliedToBreederIds, appliedWithBreedIds);
  const { formatBreederSuggestions } = useFormatBreederSuggestions(
    [applicationForm.values.breedPreferenceId].concat(appliedWithBreedIds),
  );
  const { createApplicationsForSuggestedBreeders } = useCreateApplicationsForSuggestedBreeders();
  const formattedSuggestionRows = formatBreederSuggestions(
    uniqBy('id', breedersForCurrentApplication.concat(breederForPreviousApplications)),
  );

  const suggestionsForm = useFormik({
    initialValues: {
      selectedBreeders: Array(SUGGESTED_BREEDERS_FOR_APPLICATIONS_LIMIT).fill(false),
    },
    enableReinitialize: true,
    onSubmit: () => {
      showConfirmationModal();
      Analytics.sendApplyProgressEvent(APPLICATION_STEPS.CONFIRMATION);
    },
  });

  // End suggestions logic

  useEffect(() => {
    window?.scrollTo({ left: 0, top: 0 });
  }, [currentStep, breederId]);

  useEffect(() => {
    if (latestApplicationLoading || hasStartedApplication) {
      return;
    }

    if (shouldShowPrefillModal) {
      showPrefillModal();
    }

    if (shouldShowInfoModal) {
      showInfosModal();
    }
  }, [
    latestApplication,
    latestApplicationLoading,
    hasStartedApplication,
    shouldShowInfoModal,
    shouldShowPrefillModal,
    showInfosModal,
    showPrefillModal,
  ]);

  useEffect(() => {
    // Skip saving it it already applied for the breeder
    if (applicationForBreederIdLoading || !!applicationForBreederId) {
      return;
    }

    if (Object.values(applicationForm.values).filter((val) => !!val).length) {
      saveApplication(breederId, applicationForm.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breederId, applicationForm.values, applicationForBreederId, applicationForBreederIdLoading]);

  useEffect(() => {
    if (currentAccount && currentStep === APPLICATION_STEPS.LOGIN) {
      Analytics.sendApplyProgressEvent(APPLICATION_STEPS.SUGGESTIONS);
      setCurrentStep(APPLICATION_STEPS.SUGGESTIONS);
    }
  }, [currentAccount, currentStep]);

  const confirmInfoModalHandler = useCallback(() => {
    setHasStartedApplication(true);
    closeInfosModal();
    Analytics.sendApplyProgressEvent(APPLICATION_STEPS.FORM);
  }, [closeInfosModal, setHasStartedApplication]);

  const confirmPrefillModalHandler = useCallback(() => {
    setHasStartedApplication(true);
    setShouldPrefill(true);
    closePrefillModal();
    Analytics.sendApplyProgressEvent(APPLICATION_STEPS.FORM);
  }, [closePrefillModal]);

  const stepClickHandler = useCallback(
    (step: number) => {
      if (step < currentStep) {
        setCurrentStep(step);
      }
    },
    [currentStep],
  );
  const hasADog = useMemo(() => {
    return [DogBreederApplicationOtherAnimal.DOG, DogBreederApplicationOtherAnimal.DOGS].some(
      (val) => applicationForm?.values?.otherAnimals?.includes(val),
    );
  }, [applicationForm?.values?.otherAnimals]);

  const { persistHasADog } = usePersistHasADog();

  const submitClickHandler = useCallback(async () => {
    const values = {
      ...applicationForm.values,
      genderPreferences:
        applicationForm.values.genderPreferences === 'none'
          ? [GenderEnum.F, GenderEnum.M]
          : applicationForm.values.genderPreferences,
    };

    const id = await confirmCreateApplicationFromForm({
      breederId,
      values,
    });

    // Create applications for suggested breeders
    const selectedBreeders = formattedSuggestionRows.filter(
      (_, index) => !!(suggestionsForm.values.selectedBreeders ?? [])[index],
    );
    const breederValues = selectedBreeders.reduce(
      // eslint-disable-next-line no-return-assign
      (acc, curr) => (
        (acc[`${curr.id}-${curr.breedId}`] = {
          ...omit(['questions', 'otherPreferences', 'motive'], values),
          breedPreferenceId: curr.breedId,
        }),
        acc
      ),
      {},
    );

    await createApplicationsForSuggestedBreeders(breederValues);

    if (hasADog && !currentAccount?.hasADog && currentAccount?.email) {
      persistHasADog({ email: currentAccount?.email });
    }
    clearOngoingApplication();
    closeConfirmationModal();

    Analytics.sendApplyConfirmationEvent();

    if (canSkipPayment) {
      navigateToRoute('mtApplications');
    } else {
      setCurrentStep(APPLICATION_STEPS.SUCCESS);

      if (id) {
        setCreatedApplicationId(id);
      }
    }
  }, [
    applicationForm.values,
    confirmCreateApplicationFromForm,
    breederId,
    formattedSuggestionRows,
    createApplicationsForSuggestedBreeders,
    hasADog,
    currentAccount?.hasADog,
    currentAccount?.email,
    clearOngoingApplication,
    closeConfirmationModal,
    canSkipPayment,
    suggestionsForm.values.selectedBreeders,
    persistHasADog,
    navigateToRoute,
  ]);

  useWindowConfirmUnload({
    formIsDirty: applicationForm?.dirty,
    formIsSubmitting: applicationForm?.isSubmitting,
  });

  return (
    <>
      <SeoController noIndex />
      {/* TODO SKELETON LOADING */}
      {loading && <LoadingContainer />}
      {(Number.isNaN(breederId) || (!loading && !breeder)) && <Navigate replace to="/404" />}
      {!loading && !!breeder && (
        <ApplicationPage
          onNextClick={onNextClickHandler}
          applicationForm={applicationForm}
          suggestionsForm={suggestionsForm}
          currentStep={currentStep}
          onStepClick={stepClickHandler}
          firstName={currentAccount?.firstName ?? ''}
          breederSpokenLanguages={formatLanguages(language, breeder.spokenLanguages)}
          checkoutLoading={checkoutApplicationLoading}
          breedOptions={breedOptionsForBreeder}
          suggestionRows={formattedSuggestionRows}
          suggestionRowsLoading={
            breedersForCurrentApplicationLoading || breedersForPreviousApplicationLoading
          }
          draftIsSubmitting={createDraftApplicationLoading}
          // eslint-disable-next-line @typescript-eslint/no-shadow
          onSuggestedBreederClick={(breederId) => {
            setBreederForOverviewId(breederId);
            breederOverviewModalHook.show();
          }}
          onCheckoutClick={
            createdApplicationId
              ? () => checkoutApplicationAndRedirect(createdApplicationId)
              : undefined
          }
          auth={{
            loginForm: currentAccount ? undefined : <LoginFormController />,
            joinBuyerForm: currentAccount ? undefined : (
              <JoinBuyerFormController
                createdFrom={AccountCreatedFromEnum.APPLICATION}
                hasADog={hasADog}
              />
            ),
            chosenAuth,
            onChosenAuthChange,
          }}
          header={{
            title: translate('application.title'),
            tagsPrefix: translate('application.subtitle'),
            tags: breeder?.spokenLanguages?.map((x) => ({
              content: formatLanguages(language, [x]),
              size: 'large',
              theme: 'light',
            })),
            backLink: routeLocationDescriptor('breeder', {
              params: [breeder.translations[0].url ?? ''],
            }),
          }}
        />
      )}

      <ConfirmationModal
        modal={confirmationModal}
        isSubmitting={confirmCreateApplicationLoading}
        onSubmit={submitClickHandler}
        canSkipPayment={canSkipPayment}
      />

      <InfosModal
        modal={infosModal}
        firstName={currentAccount?.firstName || ''}
        onConfirm={confirmInfoModalHandler}
      />

      <PrefillModal
        modal={prefillModal}
        firstName={currentAccount?.firstName || ''}
        onConfirm={confirmPrefillModalHandler}
      />
    </>
  );
};
